import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalUsers: 0,
    totalProducts: 0,
    totalRevenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Removed: create admin user feature

  const fetchDashboardData = async () => {
    try {
      // Fetch orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter orders: only count orders with successful payments
      const validOrders = orders.filter(order => {
        const paymentStatus = order.paymentStatus || 'pending';
        const paymentMethod = order.paymentMethod || 'cod';

        // For razorpay, only count if payment is paid
        if (paymentMethod === 'razorpay') {
          return paymentStatus === 'paid';
        }
        // For COD, count if payment is pending or paid
        return paymentStatus === 'pending' || paymentStatus === 'paid';
      });

      // Fetch users
      const usersSnapshot = await getDocs(collection(db, 'users'));

      // Fetch products
      const productsSnapshot = await getDocs(collection(db, 'products'));

      // Calculate stats using valid orders
      const totalRevenue = validOrders.reduce((sum, order) => sum + (order.total || 0), 0);

      // Get recent orders
      const recentOrdersQuery = query(
        collection(db, 'orders'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentOrdersSnapshot = await getDocs(recentOrdersQuery);
      const recentOrdersData = recentOrdersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(order => {
          const paymentStatus = order.paymentStatus || 'pending';
          const paymentMethod = order.paymentMethod || 'cod';
          if (paymentMethod === 'razorpay') {
            return paymentStatus === 'paid';
          }
          return paymentStatus === 'pending' || paymentStatus === 'paid';
        });

      setStats({
        totalOrders: validOrders.length,
        totalUsers: usersSnapshot.size,
        totalProducts: productsSnapshot.size,
        totalRevenue
      });

      setRecentOrders(recentOrdersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-gold">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-warm-gray">{title}</p>
          <p className="text-2xl font-serif font-bold text-deep-brown">{value}</p>
        </div>
        <div className="text-3xl opacity-70">{icon}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">Admin Dashboard</h1>
          <p className="text-sm md:text-base text-warm-gray font-medium">Welcome to House of Deepak Admin Panel</p>
        </div>



        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Orders"
            value={stats.totalOrders}
            icon="📦"
          />
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon="👥"
          />
          <StatCard
            title="Total Products"
            value={stats.totalProducts}
            icon="🛍️"
          />
          <StatCard
            title="Total Revenue"
            value={`₹${stats.totalRevenue.toLocaleString()}`}
            icon="💰"
          />
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-warm-gray/20">
            <h2 className="text-lg md:text-xl font-serif font-semibold text-deep-brown">Recent Orders</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-warm-gray/20">
              <thead className="bg-cream">
                <tr>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider hidden sm:table-cell">
                    Customer
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-warm-gray uppercase tracking-wider hidden md:table-cell">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-warm-gray/20">
                {recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm font-medium text-deep-brown">
                        #{order.id.substring(0, 8)}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-warm-gray hidden sm:table-cell">
                        {order.customerEmail || 'N/A'}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-semibold rounded-full ${order.status === 'completed'
                          ? 'bg-sage text-white'
                          : order.status === 'pending'
                            ? 'bg-gold text-deep-brown'
                            : 'bg-warm-gray text-white'
                          }`}>
                          {order.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-warm-gray font-medium">
                        ₹{(order.total || 0).toLocaleString()}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-warm-gray hidden md:table-cell">
                        {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-3 md:px-6 py-4 text-center text-warm-gray text-sm">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-warm-gray/20">
            <h3 className="text-lg md:text-xl font-serif font-semibold text-deep-brown mb-3 md:mb-4">Quick Actions</h3>
            <div className="space-y-2 md:space-y-3">
              <button
                onClick={() => navigate('/admin/products')}
                className="w-full bg-gold text-deep-brown px-3 md:px-4 py-2 rounded-md hover:bg-yellow-500 transition duration-200 font-medium text-sm md:text-base"
              >
                Add New Product
              </button>
              <button
                onClick={() => navigate('/admin/coupons')}
                className="w-full bg-deep-brown text-cream px-3 md:px-4 py-2 rounded-md hover:bg-warm-gray transition duration-200 font-medium text-sm md:text-base"
              >
                Create Coupon
              </button>
              <button
                onClick={() => navigate('/admin/orders')}
                className="w-full bg-sage text-white px-3 md:px-4 py-2 rounded-md hover:bg-green-700 transition duration-200 font-medium text-sm md:text-base"
              >
                View All Orders
              </button>
              {/* Removed: Create Admin User button */}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-warm-gray/20">
            <h3 className="text-lg md:text-xl font-serif font-semibold text-deep-brown mb-3 md:mb-4">Low Stock Alert</h3>
            <p className="text-warm-gray text-sm">No low stock items at the moment</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-warm-gray/20">
            <h3 className="text-lg md:text-xl font-serif font-semibold text-deep-brown mb-3 md:mb-4">System Status</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-sage rounded-full mr-2"></div>
                <span className="text-sm text-warm-gray">Database: Online</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-sage rounded-full mr-2"></div>
                <span className="text-sm text-warm-gray">Storage: Online</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-sage rounded-full mr-2"></div>
                <span className="text-sm text-warm-gray">Payments: Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;